import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Components } from './styled';
import ProjectTableHeader from '../../../../components/ProjectTableHeader';
import useFormTextState from '../../../../hooks/useFormTextState';
import ProjectTable from '../../../../components/ProjectTable';
import { filteringModel, tableColumns } from '../../../../components/ProjectTable/constants';
import { ActivityIndicatorContext } from '../../../../providers/ActivityIndicatorProvider/context';
import { Author } from '../../../../api/types/author';
import useModalState from '../../../../hooks/useModalState';
import { getAllAuthors } from '../../../../api/author';
import CreateOrEditAuthorModal from '../../../../components/modals/CreateOrEditAuthorModal';
import ProjectSearchDropdown from '../../../../components/ProjectSearchDropdown';
import ProjectPicker from '../../../../components/ProjectPicker';
import useSearchDropdown, { SearchDropdownItem } from '../../../../hooks/useSearchDropdown';
import { AuthorCategory } from '../../../../api/types/contentManagement';
import { getAllAuthorCategories } from '../../../../api/contentManagement';
import { getAllContentGroups } from '../../../../api/contentGroup';
import { getAllLessons } from '../../../../api/lesson';
import { getAllMentorShorts } from '../../../../api/mentorShort';
import { ContentGroup } from '../../../../api/types/contentGroup';
import { Lesson } from '../../../../api/types/lesson';
import { MentorShort } from '../../../../api/types/mentorShort';
import { AuthorSetupFilter, EMPTY_AUTHOR_CONTENT, buildAuthorContentMap, matchesSetupFilter } from './authorContent';

const Authors = () => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);

  const [data, setData] = useState<Author[]>([]);
  const [groups, setGroups] = useState<ContentGroup[]>([]);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [shorts, setShorts] = useState<MentorShort[]>([]);
  const [categories, setCategories] = useState<AuthorCategory[]>([]);
  const [filteredData, setFilteredData] = useState<Author[]>([]);

  const searchProps = useFormTextState();

  const categoryDropdownProps = useSearchDropdown({
    items: categories.map((category) => ({
      value: category.id,
      label: category.name,
      searchLabel: category.name,
    })),
    singleSelect: true,
    dropdownType: 'basic',
  });

  const activeDropdownProps = useSearchDropdown({
    items: [
      { value: 'active', label: 'Active', searchLabel: 'Active' },
      { value: 'inactive', label: 'Inactive', searchLabel: 'Inactive' },
    ],
    singleSelect: true,
    dropdownType: 'basic',
  });

  const setupDropdownProps = useSearchDropdown({
    items: [
      { value: 'complete', label: 'Complete', searchLabel: 'Complete' },
      { value: 'missingCourse', label: 'Missing course', searchLabel: 'Missing course' },
      { value: 'missingQa', label: 'Missing Q&A', searchLabel: 'Missing Q&A' },
    ],
    singleSelect: true,
    dropdownType: 'basic',
  });

  const createAuthorModalProps = useModalState<{ author?: Author }>();
  const editingAuthorId = createAuthorModalProps.modalState?.author?.id;

  const authorContentMap = useMemo(() => buildAuthorContentMap({ groups, lessons, shorts }), [groups, lessons, shorts]);

  // Authors, courses, lessons and shorts are fetched once, in parallel.
  const refreshData = async () => {
    setIsLoading(true);
    const [result, groupsResult, lessonsResult, shortsResult] = await Promise.all([
      getAllAuthors(),
      getAllContentGroups({ contentGroupType: 'collection' }),
      getAllLessons({}),
      getAllMentorShorts(),
    ]);
    setIsLoading(false);
    if (result && Array.isArray(result)) {
      setData(result);
    }
    setGroups(groupsResult || []);
    setLessons(lessonsResult || []);
    setShorts(shortsResult || []);
  };

  const fetchCategories = async () => {
    const result = await getAllAuthorCategories();
    if (result && Array.isArray(result)) {
      setCategories(result);
    }
  };

  const applyFilters = () => {
    let filtered = [...data];

    // Apply category filter
    if (categoryDropdownProps.selectedItems.length > 0) {
      const selectedCategoryId = categoryDropdownProps.selectedItems[0].value;
      filtered = filtered.filter((author) => author.categoryId === selectedCategoryId);
    }

    // Apply active filter (missing isActive counts as active)
    if (activeDropdownProps.selectedItems.length > 0) {
      const wantActive = activeDropdownProps.selectedItems[0].value === 'active';
      filtered = filtered.filter((author) => (author.isActive !== false) === wantActive);
    }

    // Apply setup filter (missing course / missing Q&A)
    if (setupDropdownProps.selectedItems.length > 0) {
      const setupFilter = setupDropdownProps.selectedItems[0].value as AuthorSetupFilter;
      filtered = filtered.filter((author) => matchesSetupFilter(authorContentMap[author.id], setupFilter));
    }

    setFilteredData(filtered);
  };

  useEffect(() => {
    refreshData().then(() => fetchCategories());
  }, []);

  useEffect(() => {
    applyFilters();
  }, [
    data,
    authorContentMap,
    categoryDropdownProps.selectedItems,
    activeDropdownProps.selectedItems,
    setupDropdownProps.selectedItems,
  ]);

  useEffect(() => {
    fetchCategories();
  }, []);

  const onAddAuthor = () => {
    createAuthorModalProps.open({ author: undefined });
  };
  const onEditAuthor = (id: string) => {
    const foundAuthor = data?.find((item) => item?.id === id);
    if (foundAuthor) {
      createAuthorModalProps.open({ author: foundAuthor });
    }
  };

  const onCreateOrEditModalClose = (refreshTrigger?: boolean) => {
    if (refreshTrigger) {
      refreshData?.().then();
    }
    createAuthorModalProps.onModalClose();
  };

  const _filteringModel = filteringModel([
    { columnField: 'searchValue', value: searchProps.value, operatorValue: 'contains' },
  ]);

  const displayData =
    categoryDropdownProps.selectedItems.length > 0 ||
    activeDropdownProps.selectedItems.length > 0 ||
    setupDropdownProps.selectedItems.length > 0
      ? filteredData
      : data;

  // Add category names and content counts to the data
  const dataWithCategories = displayData.map((author) => {
    const content = authorContentMap[author.id] || EMPTY_AUTHOR_CONTENT;
    return {
      ...author,
      category: categories.find((cat) => cat.id === author.categoryId)?.name || 'No Category',
      coursesCount: content.coursesCount,
      lessonsCount: content.lessonsCount,
      qaCount: content.qaCount,
      shortsCount: content.shortsCount,
    };
  });

  return (
    <Components.HeaderContainer>
      <ProjectTableHeader
        onSearchChange={searchProps.onChangeText}
        searchLabel={'Search by mentor name or ID'}
        searchPlaceholder={'Enter search term'}>
        <Components.SearchBarSeparator />
        <Components.FiltersWrapper>
          <ProjectPicker dropdownProps={categoryDropdownProps} notSetText={'All Categories'} />
          <ProjectSearchDropdown {...categoryDropdownProps} />
        </Components.FiltersWrapper>
        <Components.SearchBarSeparator />
        <Components.FiltersWrapper>
          <ProjectPicker dropdownProps={activeDropdownProps} notSetText={'All Statuses'} />
          <ProjectSearchDropdown {...activeDropdownProps} />
        </Components.FiltersWrapper>
        <Components.SearchBarSeparator />
        <Components.FiltersWrapper>
          <ProjectPicker dropdownProps={setupDropdownProps} notSetText={'All Setups'} />
          <ProjectSearchDropdown {...setupDropdownProps} />
        </Components.FiltersWrapper>
        <Components.SearchBarSeparator />
        <Components.PrimaryButton onClick={onAddAuthor}>Add Mentor</Components.PrimaryButton>
      </ProjectTableHeader>
      <Components.TableWrapper>
        <ProjectTable
          data={dataWithCategories || []}
          columns={tableColumns.authors({ onEdit: onEditAuthor })}
          filteringModel={_filteringModel}
          customPageSize={15}
          sortingModel={{ field: 'name', sort: 'asc' }}
        />
      </Components.TableWrapper>
      <CreateOrEditAuthorModal
        {...createAuthorModalProps}
        onModalClose={onCreateOrEditModalClose}
        content={editingAuthorId ? authorContentMap[editingAuthorId] || EMPTY_AUTHOR_CONTENT : undefined}
        groups={groups}
        lessons={lessons}
        authors={data}
        onContentChanged={refreshData}
      />
    </Components.HeaderContainer>
  );
};

export default Authors;
